'use client';

import type {
  CouponItemDto,
  GetCouponsCountServiceResponse,
  GetCouponsServiceResponse,
} from '@musinsa/services-curator';
import {
  COUPON_ERROR_CODES,
  isApiError,
  isUnauthorizedErrorCode,
  receiveCouponService,
} from '@musinsa/services-curator';
import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { createLoginUrl } from '../../common/auth/redirectToLogin';
import { useCuratorSnackbar } from '../../common/hooks';
import { useNavigate } from '../../common/hooks/useNavigate';
import { curatorQueries } from '../../query-factory';

interface UseReceiveCouponParams {
  shopTitle: string;
}

const DEFAULT_FAILURE_MESSAGE = '쿠폰 발급에 실패하였습니다. 다시 시도해주세요.';

// FE 고정 문구 (UI/UX 요구사항에 따라 BE 메시지와 달리 표시)
const FE_FIXED_MESSAGES: Record<string, string> = {
  [COUPON_ERROR_CODES.ALREADY_PUBLISHED]: '이미 발급받은 쿠폰입니다.',
  [COUPON_ERROR_CODES.LIMIT_EXCEEDED]: '준비된 쿠폰이 모두 소진되었습니다.',
};

export const useReceiveCoupon = ({ shopTitle }: UseReceiveCouponParams) => {
  const queryClient = useQueryClient();
  const snackbar = useCuratorSnackbar();
  const { navigate } = useNavigate();

  const updateCouponStatusInCache = (couponNo: number, status: CouponItemDto['status']) => {
    queryClient.setQueryData<InfiniteData<GetCouponsServiceResponse>>(
      curatorQueries.coupon.list(shopTitle).queryKey,
      (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            list: page.list.map((coupon) =>
              coupon.couponNo === couponNo ? { ...coupon, status } : coupon,
            ),
          })),
        };
      },
    );
  };

  const decrementAvailableCount = (amount = 1) => {
    queryClient.setQueryData<GetCouponsCountServiceResponse>(
      curatorQueries.coupon.count(shopTitle).queryKey,
      (old) => {
        if (!old) return old;
        return {
          ...old,
          availableCount: Math.max(0, old.availableCount - amount),
        };
      },
    );
  };

  const { mutate: receiveCoupon, isPending } = useMutation({
    mutationFn: (couponNo: number) => receiveCouponService({ shopTitle, couponNo }),
    onSuccess: (_data, couponNo) => {
      updateCouponStatusInCache(couponNo, 'RECEIVED');
      decrementAvailableCount();
      snackbar.success('쿠폰이 발급되었습니다.');
    },
    onError: (error) => {
      if (!isApiError(error)) {
        snackbar.error(DEFAULT_FAILURE_MESSAGE);
        return;
      }

      const errorCode = error.data?.meta?.errorCode;
      const beMessage = error.data?.meta?.message;

      if (isUnauthorizedErrorCode(errorCode)) {
        navigate(createLoginUrl(window.location.href));
        return;
      }

      // ALREADY_PUBLISHED: 재클릭 허용을 위해 상태 유지 (토스트만 표시)
      // LIMIT_EXCEEDED: BE 목록 스펙상 원래 AVAILABLE로 안 내려옴. 레이스 엣지케이스라 FE 상태 강제 변경 생략
      if (
        errorCode === COUPON_ERROR_CODES.ALREADY_PUBLISHED ||
        errorCode === COUPON_ERROR_CODES.LIMIT_EXCEEDED
      ) {
        snackbar.error(FE_FIXED_MESSAGES[errorCode]);
        return;
      }

      // 기타 에러는 BE 메시지를 우선, 없으면 기본 문구
      snackbar.error(beMessage || DEFAULT_FAILURE_MESSAGE);
    },
  });

  return { receiveCoupon, isPending };
};
